import { Settings2 } from 'lucide-react'
import { memo } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from '@/hooks/use-translation'
import {
  dataTableControlClassName,
  dataTableControlIconClassName,
} from './data-table-control-styles'
import { useColumnTitle } from './hooks/use-column-title'
import type { DataTableViewOptionsProps } from './types/data-table.types'

const EMPTY_COLUMN_VISIBILITY: Record<string, boolean> = {}

function DataTableViewOptionsComponent<TData>({
  table,
  columnVisibility = EMPTY_COLUMN_VISIBILITY,
  onColumnVisibilityChange,
}: DataTableViewOptionsProps<TData>) {
  const t = useTranslation()
  const { getColumnTitle } = useColumnTitle<TData>()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`${dataTableControlClassName} ml-auto hidden lg:flex`}
        >
          <Settings2 className={dataTableControlIconClassName} />
          {t('dataTable.columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[140px]">
        <DropdownMenuLabel className="text-xs">{t('dataTable.toggleColumns')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          className="capitalize font-medium text-xs"
          checked={(() => {
            const hideableColumns = table
              .getAllColumns()
              .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
            return hideableColumns.every((column) => columnVisibility[column.id] !== false)
          })()}
          onCheckedChange={() => {
            if (onColumnVisibilityChange) {
              onColumnVisibilityChange({})
            }
          }}
        >
          {t('dataTable.showAll')}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
          .map((column) => {
            const displayTitle = getColumnTitle(column)

            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className="capitalize text-xs"
                checked={columnVisibility[column.id] !== false}
                onCheckedChange={(value) => {
                  if (onColumnVisibilityChange) {
                    const newVisibility = { ...columnVisibility, [column.id]: value }
                    onColumnVisibilityChange(newVisibility)
                  }
                }}
              >
                {displayTitle}
              </DropdownMenuCheckboxItem>
            )
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const DataTableViewOptions = memo(
  DataTableViewOptionsComponent,
) as typeof DataTableViewOptionsComponent
